import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  ArrowLeft,
  Plus,
  Heart,
  MessageCircle,
  Share2,
  Trash2,
  BadgeCheck,
  CalendarClock,
  MapPin,
  Users2,
  Newspaper,
  PartyPopper,
  ImageOff,
} from "lucide-react";
import { toast } from "sonner";
import { api, fileUrl } from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { Avatar, Tag, Spinner, EmptyState, formatDate } from "@/components/shared";
import { ConfirmDialog } from "@/components/dialogs";
import { PostDialog, MixerDialog } from "@/components/CommunityContentDialogs";

function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      weekday: "short", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function CommunityDetail() {
  const { communityId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [community, setCommunity] = useState(location.state?.community || null);
  const [tab, setTab] = useState("posts");

  const [posts, setPosts] = useState([]);
  const [mixers, setMixers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [postOpen, setPostOpen] = useState(false);
  const [mixerOpen, setMixerOpen] = useState(false);
  const [del, setDel] = useState(null); // {kind:'post'|'mixer', id, label}

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [p, m] = await Promise.all([
        api.communityPosts(communityId),
        api.communityMixers(communityId),
      ]);
      setPosts(Array.isArray(p) ? p : p?.posts || []);
      setMixers(Array.isArray(m) ? m : m?.mixers || []);
    } catch (e) {
      setError(e.message);
      setPosts([]);
      setMixers([]);
    } finally {
      setLoading(false);
    }
  }, [communityId]);

  useEffect(() => {
    load();
    if (!community) {
      api
        .communities()
        .then((res) => {
          const list = Array.isArray(res) ? res : res?.communities || [];
          setCommunity(list.find((c) => c.community_id === communityId) || null);
        })
        .catch(() => {});
    }
  }, [load, communityId, community]);

  const doDelete = async () => {
    try {
      if (del.kind === "post") await api.deletePost(communityId, del.id);
      else await api.deleteMixer(communityId, del.id);
      toast.success(del.kind === "post" ? "Post deleted" : "Mixer deleted");
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  return (
    <div>
      <button
        onClick={() => navigate("/mypersonalworkplace/communities")}
        className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-4"
        data-testid="back-to-communities"
      >
        <ArrowLeft className="h-4 w-4" /> All communities
      </button>

      <PageHeader
        title={community?.name || "Community"}
        subtitle={
          community
            ? [community.category, `${community.member_count ?? 0} members`].filter(Boolean).join(" · ")
            : communityId
        }
        actions={
          tab === "posts" ? (
            <Button onClick={() => setPostOpen(true)} data-testid="new-post-btn">
              <Plus className="h-4 w-4" /> New post
            </Button>
          ) : (
            <Button onClick={() => setMixerOpen(true)} data-testid="new-mixer-btn">
              <Plus className="h-4 w-4" /> Schedule mixer
            </Button>
          )
        }
      />

      <Tabs value={tab} onValueChange={setTab} className="mb-5">
        <TabsList data-testid="content-tabs">
          <TabsTrigger value="posts" data-testid="tab-posts">
            <Newspaper className="h-4 w-4 mr-1.5" /> Posts
          </TabsTrigger>
          <TabsTrigger value="mixers" data-testid="tab-mixers">
            <PartyPopper className="h-4 w-4 mr-1.5" /> Mixers
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {error && (
        <div className="rounded-xl bg-destructive/15 ring-1 ring-destructive/40 px-4 py-3 text-sm mb-4" data-testid="content-error">
          {error}
        </div>
      )}

      {loading ? (
        <Spinner />
      ) : tab === "posts" ? (
        posts.length === 0 && !error ? (
          <EmptyState icon={Newspaper} title="No posts yet" hint="Publish the first post to this community feed." />
        ) : (
          <div className="space-y-3 max-w-2xl" data-testid="posts-list">
            {posts.map((p) => (
              <article key={p.post_id} className="rounded-2xl bg-card ring-1 ring-border/70 overflow-hidden" data-testid={`post-${p.post_id}`}>
                <div className="p-5">
                  <div className="flex items-center gap-3">
                    {p.author_avatar ? (
                      <Avatar src={p.author_avatar} name={p.author_name || "Amoreska"} size={40} />
                    ) : !p.author_name || /^amoreska$/i.test(p.author_name) ? (
                      <img src={`${process.env.PUBLIC_URL || ""}/logo.png`} alt="Amoreska" className="h-10 w-10 rounded-full object-cover ring-1 ring-border/60" />
                    ) : (
                      <Avatar name={p.author_name} size={40} />
                    )}
                    <div className="min-w-0">
                      <div className="font-medium flex items-center gap-1">
                        {p.author_name || "Amoreska"}
                        <BadgeCheck className="h-4 w-4 text-primary" />
                      </div>
                      <div className="text-xs text-muted-foreground">{formatDate(p.created_at)}</div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="ml-auto h-8 w-8 text-muted-foreground hover:text-destructive"
                      onClick={() => setDel({ kind: "post", id: p.post_id, label: p.title || "this post" })}
                      data-testid={`delete-post-${p.post_id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>

                  {p.title && <h3 className="font-display text-lg mt-3">{p.title}</h3>}
                  {p.body && <p className="text-sm text-foreground/90 mt-1.5 whitespace-pre-wrap">{p.body}</p>}
                </div>

                {p.image_url && (
                  <div className="bg-secondary">
                    <img src={fileUrl(p.image_url)} alt="" className="w-full max-h-96 object-cover" onError={(e) => (e.currentTarget.style.display = "none")} />
                  </div>
                )}

                <div className="flex items-center gap-6 px-5 py-3 border-t border-border/50 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1.5"><Heart className="h-4 w-4" />{p.like_count ?? 0}</span>
                  <span className="flex items-center gap-1.5"><MessageCircle className="h-4 w-4" />{p.comment_count ?? 0}</span>
                  <span className="flex items-center gap-1.5"><Share2 className="h-4 w-4" />{p.shares ?? 0}</span>
                </div>
              </article>
            ))}
          </div>
        )
      ) : mixers.length === 0 && !error ? (
        <EmptyState icon={PartyPopper} title="No mixers scheduled" hint="Schedule a live group hangout for this community." />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="mixers-list">
          {mixers.map((m) => (
            <div key={m.mixer_id} className="rounded-2xl bg-card ring-1 ring-border/70 overflow-hidden" data-testid={`mixer-${m.mixer_id}`}>
              <div className="h-28 bg-secondary relative">
                {(m.cover_url || community?.cover_url) ? (
                  <img src={m.cover_url || community?.cover_url} alt={m.title} className="h-full w-full object-cover" onError={(e) => (e.currentTarget.style.display = "none")} />
                ) : (
                  <div className="h-full w-full grid place-items-center text-muted-foreground"><ImageOff className="h-6 w-6" /></div>
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute top-2 right-2 h-8 w-8 bg-black/50 text-white hover:bg-destructive"
                  onClick={() => setDel({ kind: "mixer", id: m.mixer_id, label: m.title })}
                  data-testid={`delete-mixer-${m.mixer_id}`}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
              <div className="p-4">
                <div className="font-display text-lg">{m.title}</div>
                {m.description && <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{m.description}</p>}
                <div className="flex flex-col gap-1.5 mt-3 text-sm">
                  <span className="flex items-center gap-2 text-foreground/90"><CalendarClock className="h-4 w-4 text-primary" />{fmtDateTime(m.starts_at)}</span>
                  <span className="flex items-center gap-2 text-muted-foreground"><MapPin className="h-4 w-4" />{m.location || "Live in-app"}</span>
                </div>
                <div className="mt-3">
                  <Tag tone="blue"><Users2 className="h-3 w-3" />{m.going_count ?? 0} going</Tag>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <PostDialog open={postOpen} onOpenChange={setPostOpen} communityId={communityId} onDone={load} />
      <MixerDialog open={mixerOpen} onOpenChange={setMixerOpen} communityId={communityId} onDone={load} />
      <ConfirmDialog
        open={!!del}
        onOpenChange={(v) => !v && setDel(null)}
        title={del?.kind === "post" ? "Delete post?" : "Delete mixer?"}
        description={
          del?.kind === "post"
            ? "This removes the post and its likes/comments. This cannot be undone."
            : "This removes the mixer and clears all RSVPs. This cannot be undone."
        }
        confirmText="Delete"
        destructive
        onConfirm={doDelete}
      />
    </div>
  );
}
